import { loadTranslations } from '@angular/localize';
import { registerLocaleData } from '@angular/common';
import supportedLanguages from '../../express/supported-languages.json';

/**
 * Extracts the preferred language from the browser's navigator settings.
 * Checks against supported languages and falls back to the default if not found.
 * @param defaultLanguage - The default language to use if no supported language is found
 * @returns The detected language code
 */
export function getLanguageFromNavigator(defaultLanguage: string): string {
  const lang = navigator.languages
    .map(lang => {
      const langElements = lang.split('-');
      return langElements[0].toLocaleLowerCase();
    })
    .find(lang => {
      return supportedLanguages.includes(lang);
    });
  return lang ?? defaultLanguage;
}

/**
 * Retrieves a cookie value by name from the document.cookie string.
 * @param name - The name of the cookie to retrieve
 * @returns The cookie value or null if not found
 */
export function getLanguageFromCookie(name: string): string | null {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [key, value] = cookie.trim().split('=');
    if (key?.trim() === name) return value?.trim();
  }
  return null;
}

/**
 * Initializes the application's localization for the specified locale.
 * Loads translation files and sets up the locale data for Angular's i18n system.
 * @param locale - The locale code to initialize (e.g., 'en', 'da', 'de')
 * @returns A promise that resolves when the language is initialized
 */
export async function initLanguage(locale: string): Promise<void> {
  if (locale === "en") {
    return;
  }

  try {
    const response = await fetch("/i18n/messages." + locale + ".json");
    if (response.status !== 200) {
      console.warn('Translation not found for locale: ' + locale);
      return;
    }

    const json = await response.json();

    loadTranslations(json);
    $localize.locale = locale;

    let localeModule;
    switch (locale) {
      case 'da':
        localeModule = await import(`../../node_modules/@angular/common/locales/da`);
        break;
      case 'de':
        localeModule = await import(`../../node_modules/@angular/common/locales/de`);
        break;
      case 'nb':
        localeModule = await import(`../../node_modules/@angular/common/locales/nb`);
        break;
      case 'fr':
        localeModule = await import(`../../node_modules/@angular/common/locales/fr`);
        break;
      case 'sv':
        localeModule = await import(`../../node_modules/@angular/common/locales/sv`);
        break;
      default:
        console.warn('Translation not support for locale: ' + locale);
        return;
    }

    document.documentElement.setAttribute('lang', locale);
    registerLocaleData(localeModule.default);
  } catch (err) {
    console.error(err);
  }
}

/**
 * Returns the default application language.
 * @returns The default language code ('en')
 */
export function getDefaultAppLanguage(): string {
  return 'en';
}

/**
 * Determines the application's current language by checking the user's preferred language cookie
 * and falling back to the browser's navigator language.
 * @returns The resolved language code for the application
 */
export function getAppLanguage(): string {
  return getLanguageFromCookie('preferred-language') ?? getLanguageFromNavigator(getDefaultAppLanguage());
}
